import { useEffect, useState } from 'react'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import type { OrgTransferKind } from '@sportninja/common/features/competition/operations/utils/orgTeamsTransfer'
import Modal from 'src/components/Modal'
import ImportCSVContent from 'src/components/ImportCSV/ImportCSVContent'
import getImportConfig, {
  getConfig,
} from 'src/components/ImportCSV/get-import-config'
import ImportTeamRosterContent from 'src/components/ImportCSV/ImportTeamRoster/ImportTeamRosterContent'

interface OrgTeamsImportModalProps {
  /** Which import is open; null keeps the modal closed. */
  kind: OrgTransferKind | null
  orgId: string
  orgName: string
  onClose: () => void
  onImported: () => void
}

/**
 * The Org screen's three team imports, each in the same shared body it uses
 * there — `ImportCSVContent` for Teams, `ImportTeamRosterContent` in its
 * multi-team mode for player and team official rosters. Only the open state
 * and Escape handling are recreated here: the Org screen's wrappers render
 * their own legacy trigger buttons, which this panel does not use.
 */
const OrgTeamsImportModal = ({
  kind,
  orgId,
  orgName,
  onClose,
  onImported,
}: OrgTeamsImportModalProps) => {
  'use memo'

  const [isImporting, setIsImporting] = useState(false)

  // Matches the wrappers' own Escape handling, which the modal does not provide.
  useEffect(() => {
    if (!kind) {
      return undefined
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [kind, onClose])

  const isOfficials = kind === 'officialRosters'

  return (
    <Modal
      isOpen={kind !== null}
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
    >
      {kind === 'teams' && (
        <ImportCSVContent
          entityType={ENTITY_TYPES.org}
          id={orgId}
          importType={ENTITY_TYPES.team}
          importTemplate={getImportConfig(
            undefined,
            ENTITY_TYPES.team,
            ENTITY_TYPES.org
          )}
          loading={isImporting}
          setLoading={setIsImporting}
          toggle={onClose}
          onSuccess={onImported}
        />
      )}
      {(kind === 'playerRosters' || kind === 'officialRosters') && (
        <ImportTeamRosterContent
          orgId={orgId}
          isMultiTeamRoster
          rosterType={isOfficials ? 'teamOfficials' : 'players'}
          teamName={orgName}
          importTemplate={
            isOfficials
              ? getConfig().multiTeamOfficials
              : getConfig().multiTeamRoster
          }
          loading={isImporting}
          setLoading={setIsImporting}
          toggle={onClose}
          onSuccess={onImported}
        />
      )}
    </Modal>
  )
}

export default OrgTeamsImportModal
